import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { MentionTextarea } from "@/components/ui/mention-textarea";
import { saveMentions } from "@/lib/mention-utils";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { MessageSquare, ArrowLeft, Send, Plus } from "lucide-react";
import { toast } from "sonner";

interface DiscussionReply {
  id: string;
  content: string;
  created_at: string;
  user_id: string;
  discussion_id: string;
  profiles?: {
    username: string | null;
    avatar_url: string | null;
  } | null;
}

interface Discussion {
  id: string;
  title: string;
  content: string;
  created_at: string;
  user_id: string;
  profiles?: {
    username: string | null;
    avatar_url: string | null;
  } | null;
  replies?: DiscussionReply[];
}

const DashboardDiscussions = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [discussions, setDiscussions] = useState<Discussion[]>([]);
  const [newDiscussion, setNewDiscussion] = useState({ title: "", content: "" });
  const [isCreating, setIsCreating] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedDiscussion, setSelectedDiscussion] = useState<Discussion | null>(null);
  const [replyContent, setReplyContent] = useState("");
  const [isReplying, setIsReplying] = useState(false);
  const [replyDialogOpen, setReplyDialogOpen] = useState(false);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      navigate("/auth");
      return;
    }
    loadDiscussions();
  };

  const loadDiscussions = async () => {
    try {
      const { data, error } = await supabase
        .from("discussions")
        .select("*, profiles(username, avatar_url)")
        .order("created_at", { ascending: false });

      if (error) {
        toast.error("Error loading discussions");
        return;
      }

      const userIds = new Set(data?.map(d => d.user_id) || []);
      const { data: profileData } = await supabase
        .from("profiles")
        .select("id, username, avatar_url")
        .in("id", Array.from(userIds));

      const profileMap = new Map(profileData?.map(p => [p.id, p]) || []);

      // Fetch replies for each discussion
      const { data: repliesData } = await supabase
        .from("discussion_replies")
        .select("*, profiles(username, avatar_url)")
        .order("created_at", { ascending: true });

      const repliesByDiscussion = new Map<string, DiscussionReply[]>();
      repliesData?.forEach(reply => {
        if (!repliesByDiscussion.has(reply.discussion_id)) {
          repliesByDiscussion.set(reply.discussion_id, []);
        }
        repliesByDiscussion.get(reply.discussion_id)?.push({
          ...reply,
          profiles: profileMap.get(reply.user_id) || null
        });
      });

      const discussionsWithProfiles = data?.map(discussion => ({
        ...discussion,
        profiles: profileMap.get(discussion.user_id) || null,
        replies: repliesByDiscussion.get(discussion.id) || []
      }));

      setDiscussions(discussionsWithProfiles);
    } catch (error) {
      console.error("Error loading discussions:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDiscussion = async () => {
    if (!newDiscussion.title.trim() || !newDiscussion.content.trim()) {
      toast.error("Please fill in all fields");
      return;
    }

    setIsCreating(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error("You must be logged in");
        return;
      }

      const { data, error } = await supabase
        .from("discussions")
        .insert([{
          title: newDiscussion.title,
          content: newDiscussion.content
        } as any])
        .select()
        .single();

      if (error) {
        toast.error("Error creating discussion");
      } else {
        // Save mentions if any
        if (data) {
          await saveMentions(newDiscussion.content, "discussion", data.id);
        }
        toast.success("Discussion created successfully");
        setNewDiscussion({ title: "", content: "" });
        setIsDialogOpen(false);
        loadDiscussions();
      }
    } finally {
      setIsCreating(false);
    }
  };

  const handleReply = async () => {
    if (!replyContent.trim() || !selectedDiscussion) {
      toast.error("Please enter a reply");
      return;
    }

    setIsReplying(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error("You must be logged in");
        return;
      }

      const { data, error } = await supabase
        .from("discussion_replies")
        .insert([{
          discussion_id: selectedDiscussion.id,
          content: replyContent
        } as any])
        .select()
        .single();

      if (error) {
        toast.error("Error posting reply");
      } else {
        // Save mentions if any
        if (data) {
          await saveMentions(replyContent, "discussion_reply", data.id);
        }
        toast.success("Reply posted successfully");
        setReplyContent("");
        setReplyDialogOpen(false);
        loadDiscussions();
      }
    } finally {
      setIsReplying(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-background to-muted/30 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/30">
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center gap-4 mb-8">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/dashboard")}
            className="hover:bg-primary/10"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-4xl font-bold">Community Discussions</h1>
            <p className="text-muted-foreground mt-2">
              Connect with other members, ask questions, and share insights
            </p>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button className="gap-2">
                <Plus className="h-4 w-4" />
                New Discussion
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Start a New Discussion</DialogTitle>
                <DialogDescription>
                  Share your thoughts, ask questions, or start a conversation
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <Input
                  placeholder="Discussion title"
                  value={newDiscussion.title}
                  onChange={(e) => setNewDiscussion({ ...newDiscussion, title: e.target.value })}
                />
                <MentionTextarea
                  placeholder="What's on your mind? (use @ to mention someone)"
                  value={newDiscussion.content}
                  onChange={(e) => setNewDiscussion({ ...newDiscussion, content: e.target.value })}
                  rows={5}
                />
                <Button
                  onClick={handleCreateDiscussion}
                  disabled={isCreating}
                  className="w-full"
                >
                  {isCreating ? "Creating..." : "Create Discussion"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {discussions.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <MessageSquare className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-xl font-semibold mb-2">No Discussions Yet</h3>
              <p className="text-muted-foreground mb-4">Be the first to start a conversation!</p>
              <Button onClick={() => setIsDialogOpen(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Start Discussion
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {discussions.map((discussion) => (
              <Card key={discussion.id} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3 flex-1">
                      <Avatar>
                        <AvatarImage src={discussion.profiles?.avatar_url || ""} />
                        <AvatarFallback>
                          {discussion.profiles?.username?.[0]?.toUpperCase() || "U"}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <CardTitle className="text-xl mb-2">{discussion.title}</CardTitle>
                        <p className="text-sm text-muted-foreground mb-2">
                          by {discussion.profiles?.username || "Anonymous"} •{" "}
                          {new Date(discussion.created_at).toLocaleDateString()}
                        </p>
                        <CardDescription className="text-base">
                          {discussion.content}
                        </CardDescription>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {discussion.replies && discussion.replies.length > 0 && (
                      <div className="border-t pt-4 space-y-3">
                        <p className="text-sm font-semibold text-muted-foreground">
                          {discussion.replies.length} {discussion.replies.length === 1 ? "Reply" : "Replies"}
                        </p>
                        {discussion.replies.map((reply) => (
                          <div key={reply.id} className="flex gap-3 ml-4">
                            <Avatar className="h-8 w-8">
                              <AvatarImage src={reply.profiles?.avatar_url || ""} />
                              <AvatarFallback>
                                {reply.profiles?.username?.[0]?.toUpperCase() || "U"}
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex-1">
                              <p className="text-sm font-medium">
                                {reply.profiles?.username || "Anonymous"}
                              </p>
                              <p className="text-sm text-muted-foreground">{reply.content}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSelectedDiscussion(discussion);
                        setReplyDialogOpen(true);
                      }}
                      className="gap-2"
                    >
                      <Send className="h-4 w-4" />
                      Reply
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <Dialog open={replyDialogOpen} onOpenChange={setReplyDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Reply to Discussion</DialogTitle>
              <DialogDescription>
                {selectedDiscussion?.title}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <MentionTextarea
                placeholder="Your reply... (use @ to mention someone)"
                value={replyContent}
                onChange={(e) => setReplyContent(e.target.value)}
                rows={5}
              />
              <Button
                onClick={handleReply}
                disabled={isReplying}
                className="w-full"
              >
                {isReplying ? "Posting..." : "Post Reply"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default DashboardDiscussions;
